import React, { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  LabelList,
  Cell,
} from "recharts";
import CustomButton from "../customButtom/CustomButton";
import { useNavigate } from "react-router-dom";
import { Brain } from "lucide-react";

export default function BarChartComponent({
  title,
  data = [],
  bars,
  height = 300,
  yAxisRange = false,
  colors,
  intervalOption = false,
  criticalThinkingData,
}) {
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const isEmpty = !data || data.length === 0;

  const getColor = (bar, index) => {
    if (bar.dataKey === "benchmark") {
      return bar.color;
    }
    if (colors?.length) {
      return colors[index % colors.length];
    }
    return bar.color;
  };

  const CustomXAxisTick = ({ x, y, payload }) => {
    const words = payload.value.split(" ");
    const lines = [];
    const wordsPerLine = 1;

    for (let i = 0; i < words.length; i += wordsPerLine) {
      lines.push(words.slice(i, i + wordsPerLine).join(" "));
    }

    return (
      <g transform={`translate(${x},${y})`}>
        {lines.map((line, index) => (
          <text
            key={index}
            x={0}
            y={index * 12}
            textAnchor="middle"
            className="text-xs fill-gray-700"
          >
            {line}
          </text>
        ))}
      </g>
    );
  };

  const customYAxisTicks = () => {
    const maxValue = yAxisRange
      ? 100
      : Math.max(
          ...data.flatMap((item) => bars.map((bar) => item[bar.dataKey] || 0))
        ) + 10;

    const ticks = [];
    for (let i = 0; i <= maxValue; i += 5) {
      ticks.push(i);
    }
    return ticks;
  };

  return (
    <div className="bg-white rounded-lg shadow-md border p-1">
      {title && (
        <div className="flex gap-2 items-center justify-center p-4">
          <div className="w-10 h-10 bg-gradient-to-r from-pink-500 to-purple-500 rounded-lg flex items-center justify-center">
            <Brain className="w-7 h-7 text-white" />
          </div>
          <span className="font-bold text-lg text-gray-800">{title}</span>
        </div>
      )}

      {isEmpty ? (
        <div className="flex flex-col items-center justify-center p-8">
          <p className="text-xl font-semibold text-gray-700 mb-4">
            You have not taken the AI Literacy Quiz
          </p>
          <CustomButton
            label="Take the Quiz now"
            onClick={() => navigate("/dashboard/career-assessment/ai-literacy-assessment")}
          />
        </div>
      ) : (
        <div className="relative">
          <ResponsiveContainer width="100%" height={height}>
            <BarChart
              data={data}
              margin={{ top: 0, right: 80, left: 40, bottom: isMobile ? 80 : 0 }}
              width={500}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
              <XAxis
                dataKey="name"
                height={isMobile ? 80 : 60}
                interval={0}
                tickMargin={10}
                tick={<CustomXAxisTick />}
              />
              <YAxis
                domain={yAxisRange ? [0, 100] : [0, "dataMax + 10"]}
                ticks={customYAxisTicks()}
                tickFormatter={(value) => value}
                padding={{ top: 20 }}
                width={30}
                tick={{ fontSize: 10 }}
              />
              <Tooltip />
              {bars?.map((bar, barIndex) => (
                <Bar 
                  key={barIndex} 
                  dataKey={bar.dataKey}
                  barSize={30}
                >
                  {data.map((_, i) => (
                    <Cell key={`cell-${i}`} fill={getColor(bar, i)} />
                  ))}
                  <LabelList
                    dataKey={bar.dataKey}
                    position="top"
                    className="text-xs"
                    formatter={(value) => (value ? `${value}` : "")}
                  />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
          
          {criticalThinkingData && (
            <div className="absolute top-4 right-8 bg-white border rounded-lg p-3 shadow-sm">
              <h3 className="font-bold text-sm text-gray-800 mb-2">
                Critical Thinking About AI
              </h3>
              <div className="text-xs text-gray-600 space-y-1">
                <div>score : {criticalThinkingData.score}</div>
                <div>benchmark : {criticalThinkingData.benchmark}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {!isEmpty && (
        <div className="p-2 flex justify-center">
          <div className="flex flex-wrap items-center justify-center gap-4">
            {bars?.map((bar, index) => (
              <div key={index}>
                <div className="flex gap-2 items-center">
                  <div
                    className="w-2 h-2"
                    style={{ 
                      backgroundColor: bar.dataKey === "score" && colors?.length 
                        ? "#9C27B0"
                        : bar.color 
                    }}
                  ></div>
                  <span className="text-xs">{bar.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
